import moment from "moment";
import { ScholarshipDataCollection } from "../../api";

// TYPES

export const scholarshipCategories = [
  "Active",
  "Coming Soon",
  "Expired",
] as const;
export type ScholarshipCategory = typeof scholarshipCategories[number];

type ScholarshipFilterCallback = (
  scholarship: ScholarshipDataCollection
) => boolean;

type ScholarshipSortCallback = (
  a: ScholarshipDataCollection,
  b: ScholarshipDataCollection
) => number;

// HELPERS

/**
 * Generate callback for `sort` function based on date key in `ScholarshipDataCollection`.
 *
 * @param  {'openDate'|'closeDate'} keyName - the date key to sort by.
 * @param  {'ascending'|'descending'} [direction='ascending'] - direction to sort dates in.
 * @param  {'top'|'bottom'} [missingDatesLocation='bottom'] - where to put items without the date key defined.
 * @returns {ScholarshipSortCallback}
 */
const generateSortCallback = (
  keyName: "openDate" | "closeDate",
  direction: "ascending" | "descending" = "ascending",
  missingDatesLocation: "top" | "bottom" = "bottom"
): ScholarshipSortCallback => {
  // handle inversion of sort direction based on `direction` value
  const directionIndicator = direction === "ascending" ? 1 : -1;

  // handle inversion of sort direction based on `direction` value
  const missingDatesLocationIndicator =
    missingDatesLocation === "bottom" ? 1 : -1;

  return (a, b) => {
    const [aDate, bDate] = [a[keyName], b[keyName]].map((date) =>
      typeof date == "string" ? moment(date) : undefined
    );

    // in case neither has date, do not sort
    if (!aDate && !bDate) {
      return 0;
    }

    // in case `a` has no date
    if (!aDate && bDate) {
      return 1 * missingDatesLocationIndicator;
    }

    // in case `b` has no date
    if (aDate && !bDate) {
      return -1 * missingDatesLocationIndicator;
    }

    // in case both have date, sort by date
    if (aDate && bDate) {
      return aDate.isSame(bDate)
        ? 0
        : aDate.isAfter(bDate)
        ? 1 * directionIndicator
        : -1 * directionIndicator;
    }

    return 0;
  };
};

// Collection of filter functions to filter scholarships based on state of `sortingCategory` state in `Results`
export const filterScholarshipCollection: {
  [key in ScholarshipCategory]: ScholarshipFilterCallback;
} = {
  Active: (scholarship): boolean => {
    const today = moment();

    const [openDate, closeDate] = [
      scholarship.openDate,
      scholarship.closeDate,
    ].map((date) => (typeof date == "string" ? moment(date) : undefined));

    // no open or close date for scholarship
    if (!openDate && !closeDate) {
      return true;
    }

    // no close date for scholarship
    if (openDate && !closeDate) {
      return openDate <= today;
    }

    // no open date for scholarship
    if (!openDate && closeDate) {
      return closeDate >= today;
    }

    // has both dates
    if (openDate && closeDate) {
      return openDate.isSameOrBefore(today) && closeDate.isSameOrAfter(today);
    }

    return true;
  },
  "Coming Soon": (scholarship): boolean => {
    if (!scholarship.openDate) {
      return false;
    }

    const today = moment();
    const openDate = moment(scholarship.openDate);

    return openDate.isAfter(today) && openDate.diff(today, "months") < 1;
  },
  Expired: (scholarship): boolean => {
    if (!scholarship.closeDate) {
      return false;
    }

    const today = moment();
    console.log(scholarship.closeDate);
    const closeDate = moment(scholarship.closeDate);

    return closeDate.isBefore(today);
  },
};

// Collection of sort functions to sort scholarships based on state of `sortingCategory` state in `Results`
export const sortScholarshipCollection: {
  [key in ScholarshipCategory]: ScholarshipSortCallback;
} = {
  Active: generateSortCallback("closeDate"),
  "Coming Soon": generateSortCallback("openDate"),
  Expired: generateSortCallback("closeDate", "descending"),
};
